import React, { useMemo } from 'react';
import { Badge, Chip } from '@mui/material';
import type { McaDisplayAttribute } from '../../graphql/types';
import { MerchantCashAdvanceStatus, NonWorkingDay, Rights } from '../../graphql/types';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { useNavigate } from 'react-router-dom';
import { statusTextColor } from './ChangeMcaStatus';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import { underscoreToSpace } from '../../utils/strings';
import StatusChip from '../../components/StatusChip';
import { Box } from '@mui/system';
import { countNotPayingBankingDays } from '../../utils/bankDayUtils';
export type McaCornerBadge = { label: string; color: string };


enum PageNavigationContext {
  CASH_ADVANCE_PAGE = '/merchants/mcas',
  MY_PORTFOLIO_PAGE = '/my-portfolio',
  PORTFOLIOS_PAGE = '/portfolios',
  PORTFOLIO_DETAIL_PAGE_BASE = '/portfolio'
}

function normalizePath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

function isSpecialNavigationContext(pathname?: string): boolean {
  if (!pathname) pathname = window.location.pathname;
  pathname = normalizePath(pathname);

  if (pathname === PageNavigationContext.CASH_ADVANCE_PAGE) {
    return true;
  }

  if (pathname === PageNavigationContext.MY_PORTFOLIO_PAGE) {
    return true;
  }

  if (pathname === PageNavigationContext.PORTFOLIOS_PAGE) {
    return true;
  }

  const portfolioBase = PageNavigationContext.PORTFOLIO_DETAIL_PAGE_BASE;
  const portfolioDetailRegex = new RegExp(`^${portfolioBase}/[^/]+$`);
  if (portfolioDetailRegex.test(pathname)) {
    return true;
  }

  return false;
}

export default function McaCommisionStatusChipRenderer({ value, node }: ICellRendererParams) {
  if (node.isRowPinned()) return null;

  const isPaid = Boolean(value);

  return (
    <Box display="flex" alignItems="center" height="100%">
      <Chip
        label={isPaid ? 'PAID' : 'UNPAID'}
        size="small"
        variant="filled"
        sx={{
          height: 20,
          borderRadius: '4px',
          bgcolor: isPaid ? 'success.main' : 'grey.400',
          color: isPaid ? 'common.white' : 'grey.900'
        }}
      />
    </Box>
  );
}

const statusColor = (status: MerchantCashAdvanceStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case MerchantCashAdvanceStatus.Open:
      return 'info';
    case MerchantCashAdvanceStatus.Attorney:
    case MerchantCashAdvanceStatus.InHouseLegal:
    case MerchantCashAdvanceStatus.Collections:
      return 'error';
    case MerchantCashAdvanceStatus.NotPaying:
      return 'warning';
    case MerchantCashAdvanceStatus.PrepLegal:
      return 'error';
    case MerchantCashAdvanceStatus.Slow:
      return 'secondary';
    case MerchantCashAdvanceStatus.WrittenOff:
      return 'primary';
    case MerchantCashAdvanceStatus.Completed:
      return 'success';
    case MerchantCashAdvanceStatus.Settled:
      return 'success';
    case MerchantCashAdvanceStatus.VerySlow:
      return 'success';
    case MerchantCashAdvanceStatus.Paused:
      return 'success';
    case MerchantCashAdvanceStatus.Resumed:
      return 'success';
    default:
      return 'default';
  }
};

const theme = createTheme({
  typography: {
    fontFamily: `"Public Sans", sans-serif`
  },
  palette: {
    contrastThreshold: 4.5,
    info: {
      main: '#189ae0',
      contrastText: '#fff'
    },
    primary: {
      main: '#bfbfbf',
      contrastText: '#fff'
    },
    error: {
      main: '#FF4528',
      contrastText: '#fff'
    },
    warning: {
      main: '#FFB814',
      contrastText: '#fff'
    },
    secondary: {
      main: '#15d8ea',
      contrastText: '#fff'
    }
  }
});

export const BootstrapTooltip = (props: TooltipProps) => {
  return (
    <Tooltip
      {...props}
      arrow
      slotProps={{
        popper: {
          sx: {
            [`& .MuiTooltip-tooltip`]: {
              backgroundColor: theme.palette.common.black,
              color: 'white',
              fontSize: '14px'
            },
            [`& .MuiTooltip-arrow`]: {
              color: theme.palette.common.black
            }
          }
        }
      }}
    />
  );
};

export const getStatusLabel = (status: MerchantCashAdvanceStatus): string => {
  if (!status) {
    return '';
  }
  if (status === MerchantCashAdvanceStatus.InHouseLegal) {
    return 'IN-HOUSE LEGAL';
  }
  return underscoreToSpace(status);
};

export const statusTooltipTitleMap = Object.freeze<Record<MerchantCashAdvanceStatus, string>>({
  [MerchantCashAdvanceStatus.Open]: 'Performance is above 60%',
  [MerchantCashAdvanceStatus.Completed]: 'Paid in Full',
  [MerchantCashAdvanceStatus.Attorney]: 'Paying slowly, sporadically, or with wide gaps between collections via attorneys',
  [MerchantCashAdvanceStatus.Collections]: 'File sent to the attorney, no collections being made at the moment',
  [MerchantCashAdvanceStatus.Slow]: 'Performance is between 30% and 60%',
  [MerchantCashAdvanceStatus.WrittenOff]: 'All collection efforts have been exhausted',
  [MerchantCashAdvanceStatus.NotPaying]: 'Recurring payment stopped',
  [MerchantCashAdvanceStatus.PrepLegal]: 'File is being prepared for submission to attorney',
  [MerchantCashAdvanceStatus.Settled]: 'Settled for less than full amount',
  [MerchantCashAdvanceStatus.VerySlow]: 'Performance is below 30%',
  [MerchantCashAdvanceStatus.Resumed]: 'Recurring payment has been resumed',
  [MerchantCashAdvanceStatus.Paused]: 'Recurring payment has been paused',
  [MerchantCashAdvanceStatus.Deleted]: 'File deleted',
  [MerchantCashAdvanceStatus.InHouseLegal]: 'File has been assigned for collection to the in-house attorney'
});

export const getStatusTooltipTitle = (status: MerchantCashAdvanceStatus): string => statusTooltipTitleMap[status] || status;

export const filterMcaStatusValues = [
  MerchantCashAdvanceStatus.Open,
  MerchantCashAdvanceStatus.Collections,
  MerchantCashAdvanceStatus.NotPaying,
  MerchantCashAdvanceStatus.PrepLegal,
  MerchantCashAdvanceStatus.Slow,
  MerchantCashAdvanceStatus.WrittenOff,
  MerchantCashAdvanceStatus.Attorney,
  MerchantCashAdvanceStatus.InHouseLegal,
  MerchantCashAdvanceStatus.Completed,
  MerchantCashAdvanceStatus.Paused,
  MerchantCashAdvanceStatus.Settled,
  MerchantCashAdvanceStatus.Slow,
  MerchantCashAdvanceStatus.VerySlow,
  MerchantCashAdvanceStatus.Deleted,
  MerchantCashAdvanceStatus.Resumed
];


export function buildMcaStatusCornerBadge(
  row: { displayAttributes?: string[] | null } | null,
  attributeRows: McaDisplayAttribute[]
): McaCornerBadge[] {
  if (!row?.displayAttributes?.length || !attributeRows.length) return [];
  return row.displayAttributes
    .map((raw) => {
      const label = raw.trim();
      if (!label) return null;
      const key = label.toUpperCase();
      const color = attributeRows.find((r) => r.key.trim().toUpperCase() === key)?.color;
      return color ? { label, color } : null;
    })
    .filter((b): b is McaCornerBadge => b !== null);
}

type McaDisplayAttributeProfileChipProps = {
  displayAttributes?: string[] | null;
  attributeCatalog: McaDisplayAttribute[];
};

export function McaDisplayAttributeProfileChip({ displayAttributes, attributeCatalog }: McaDisplayAttributeProfileChipProps) {
  const badges = useMemo(
    () => buildMcaStatusCornerBadge({ displayAttributes }, attributeCatalog),
    [displayAttributes, attributeCatalog]
  );

  if (!badges.length) return null;

  return (
    <>
      {badges.map((badge) => {
        const key = badge.label.trim().toUpperCase();
        const description = attributeCatalog.find((r) => r.key.trim().toUpperCase() === key)?.description ?? '';
        const chip = (
          <Chip
            key={badge.label}
            label={badge.label}
            size="small"
            sx={{ borderRadius: '4px', height: '20px', color: '#fff' }}
            style={{ backgroundColor: badge.color }}
          />
        );
        return description ? (
          <BootstrapTooltip key={badge.label} title={description} placement="bottom">
            {chip}
          </BootstrapTooltip>
        ) : chip;
      })}
    </>
  );
}

export const McaStatusChipCellRenderer = ({
  node,
  data,
  openInNewTab,
  useCompactViewStyle,
  isSyndicator,
  nonWorkingDays,
  mcaCornerBadge
}: ICellRendererParams & {
  openInNewTab?: boolean;
  isSyndicator?: boolean;
  useCompactViewStyle?: boolean;
  nonWorkingDays?: NonWorkingDay[];
  mcaCornerBadge?: McaCornerBadge[];
}) => {
  if (node.isRowPinned()) {
    return null;
  }
  //TODO (artem): ugly fix for cell rerender by socket event
  const canViewMcaDetails =
    data?.currentUserRights && data?.currentUserRights.length ? data?.currentUserRights?.includes(Rights.ViewMcaDetails) : true;

  const startDate = isSyndicator ? data?.mcaStartDate : data?.startDate;
  let notPayingBadge: string | null = null;
  if ((isSyndicator ? data.mcaStatus : data?.status) === MerchantCashAdvanceStatus.NotPaying) {
    const days = countNotPayingBankingDays(nonWorkingDays || [], data.lastPmtDay, startDate, new Date());
    if (days != null) {
      notPayingBadge = `${days} d`;
    }
  }

  const badgeFontSize = useCompactViewStyle ? '8px' : '11px';
  const badgeSx = { '& > .MuiBadge-badge': { fontSize: badgeFontSize, bottom: 4, right: -8, top: 'auto' } };
  const [primaryBadge, typeBadge] = mcaCornerBadge || [];

  const cornerBadgeStyle = (badge: McaCornerBadge, left: number) => ({
    backgroundColor: badge.color,
    color: '#fff',
    fontSize: badgeFontSize,
    top: 4,
    left
  });

  const statusChip = (
    <Badge
      badgeContent={notPayingBadge}
      color="error"
      sx={badgeSx}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <McaStatusChip
        id={isSyndicator ? data.mcaId : data.id}
        status={isSyndicator ? data.mcaStatus : data.status}
        navigateToDetails={canViewMcaDetails}
        {...(!isSyndicator && { tooltipPlacement: 'bottom' })}
        useCompactViewStyle={!!useCompactViewStyle}
        investorId={isSyndicator ? data.investorId : ''}
        openInNewTab={openInNewTab}
      />
    </Badge>
  );

  const withTypeBadge = typeBadge ? (
    <Badge
      badgeContent={typeBadge.label}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      componentsProps={{ badge: { style: cornerBadgeStyle(typeBadge, -8) } }}
    >
      {statusChip}
    </Badge>
  ) : statusChip;

  return (
    <Badge
      badgeContent={primaryBadge ? primaryBadge.label : null}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      componentsProps={{
        badge: primaryBadge ? { style: cornerBadgeStyle(primaryBadge, typeBadge ? -24 : -8) } : undefined
      }}
    >
      {withTypeBadge}
    </Badge>
  );
};

type McaStatusChipProps = {
  id: string;
  status: MerchantCashAdvanceStatus;
  label?: string;
  navigateToDetails?: boolean;
  tooltipPlacement?: TooltipProps['placement'];
  openInNewTab?: boolean;
  openOnSameContext?: boolean;
  investorId?: string;
  useCompactViewStyle?: boolean;
};

export const McaStatusChip = ({
  id,
  status,
  navigateToDetails = false,
  label,
  tooltipPlacement = 'right',
  openInNewTab = false,
  openOnSameContext = false,
  investorId,
  useCompactViewStyle = false
}: McaStatusChipProps) => {
  const navigate = useNavigate();

  // Memoize computed values
  const formattedStatus = useMemo(() => getStatusLabel(status), [status]);
  const tooltipTitle = useMemo(
    () => (label ? `${formattedStatus}: ${getStatusTooltipTitle(status)}` : getStatusTooltipTitle(status)),
    [label, formattedStatus, status]
  );
  const chipLabel = useMemo(() => label || formattedStatus, [label, formattedStatus]);
  const mcaPath = useMemo(() => (!!investorId ? `/portfolio/${investorId}/mca/${id}` : `/merchants/mca/${id}`), [id, investorId]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (navigateToDetails) {
      if (openInNewTab) {
        window.open(mcaPath, '_blank');
      } else {
        if (isSpecialNavigationContext()) {
          navigate(`${window.location.pathname}?id=${id}`, {
            state: {
              mcaId: id,
              fromStatusChip: true
            }
          });
        } else if (normalizePath(window.location.pathname) === '/funding-pipeline') {
          navigate(`/funding-pipeline?mcaId=${id}`, {
            state: { mcaId: id, fromStatusChip: true }
          });
        } else {
          navigate(mcaPath, {
            state: { fromStatusChip: true }
          });
        }
      }
    }
    return false;
  };

  if (!status) {
    return <></>;
  }

  return (
    <ThemeProvider theme={theme}>
      <BootstrapTooltip title={tooltipTitle} placement={tooltipPlacement}>
        {useCompactViewStyle ? (
          <StatusChip
            sx={{
              borderRadius: '4px',
              backgroundColor: statusTextColor(status),
              height: '20px'
            }}
            label={chipLabel}
            color={statusColor(status)}
            size="small"
            variant="light"
            onClick={handleClick}
          />
        ) : (
          <Chip
            sx={{
              borderRadius: '4px',
              backgroundColor: statusTextColor(status),
              height: '20px'
            }}
            label={chipLabel}
            color={statusColor(status)}
            size="small"
            variant="light"
            onClick={handleClick}
          />
        )}
      </BootstrapTooltip>
    </ThemeProvider>
  );
};

McaStatusChip.displayName = 'McaStatusChip';

export const McaStatusChipWithBadges = () => {};
